<div [formGroup]="form" class="flex min-h-[40px] flex-wrap items-center gap-2 py-1.5">
  <!-- Label -->
  <div class="text-secondary flex shrink-0 items-center gap-1">
    <mat-icon class="!h-3.5 !w-3.5 !text-sm !leading-none">tune</mat-icon>
    <span class="text-[11px] font-semibold uppercase tracking-wider">Filters</span>
  </div>

  <div class="h-4 w-px shrink-0 bg-gray-200"></div>

  <!-- Community chip-select -->
  @if (config.community) {
    <button type="button" class="filter-chip" [class.filter-chip--active]="form.get('association_id')?.value" #communityTrigger="matMenuTrigger" [matMenuTriggerFor]="communityMenu">
      <span class="filter-chip__label">Community</span>
      <span class="filter-chip__value">{{ selectedAssociationName }}</span>
      <mat-icon class="filter-chip__arrow">expand_more</mat-icon>
    </button>
    <mat-menu #communityMenu="matMenu" panelClass="filter-chip-menu">
      <!-- Search box — stopPropagation here keeps the menu open while typing -->
      <div class="filter-chip-menu__search" (click)="$event.stopPropagation()">
        <mat-icon class="filter-chip-menu__search-icon">search</mat-icon>
        <input
          type="text"
          placeholder="Search community…"
          class="filter-chip-menu__search-input"
          [(ngModel)]="communitySearch"
          [ngModelOptions]="{ standalone: true }"
          (keydown)="$event.stopPropagation()"
          autocomplete="off"
        />
        @if (communitySearch) {
          <button type="button" class="filter-chip-menu__search-clear" (click)="communitySearch = ''">
            <mat-icon style="font-size: 14px; height: 14px; width: 14px; line-height: 14px">close</mat-icon>
          </button>
        }
      </div>
      <!-- Items — no stopPropagation so the menu closes on selection -->
      <div style="max-height: 220px; overflow-y: auto; overflow-x: hidden">
        @if (!communitySearch) {
          <button mat-menu-item (click)="setField('association_id', null); communitySearch = ''; communityTrigger.closeMenu()" [class.selected]="!form.get('association_id')?.value">
            <span>All Communities</span>
          </button>
        }
        @for (a of filteredAssociations; track a.id) {
          <button mat-menu-item (click)="setField('association_id', a.id); communitySearch = ''; communityTrigger.closeMenu()" [class.selected]="form.get('association_id')?.value === a.id">
            {{ a.name }}
          </button>
        }
        @if (filteredAssociations.length === 0) {
          <div class="filter-chip-menu__empty">No results</div>
        }
      </div>
    </mat-menu>
  }

  <!-- Period pill toggle group -->
  @if (config.period) {
    <div class="-mb-0.5 w-full shrink-0 overflow-x-auto pb-0.5 sm:w-auto">
      <div class="flex w-max items-center gap-px rounded-lg border border-gray-200 bg-gray-50/80 p-0.5">
        @for (p of periodOptions; track p.value) {
          @if (p.value !== 'custom') {
            <button type="button" class="period-pill" [class.period-pill--active]="form.get('period')?.value === p.value" (click)="setPeriod(p.value)">{{ p.short }}</button>
          }
        }
        <button type="button" class="period-pill" [class.period-pill--active]="form.get('period')?.value === 'custom'" (click)="setPeriod('custom')">Custom</button>
      </div>
    </div>
  }

  <!-- Compare to chip -->
  @if (config.compareTo && compareOptions.length > 0) {
    <button type="button" class="filter-chip" [class.filter-chip--active]="form.get('compare_to')?.value" #compareTrigger="matMenuTrigger" [matMenuTriggerFor]="compareMenu">
      <span class="filter-chip__label">Compare to</span>
      <span class="filter-chip__value">{{ selectedCompareLabel }}</span>
      <mat-icon class="filter-chip__arrow">expand_more</mat-icon>
    </button>
    <mat-menu #compareMenu="matMenu" panelClass="filter-chip-menu">
      <div style="max-height: 260px; overflow-y: auto; overflow-x: hidden">
        <button mat-menu-item (click)="setField('compare_to', null); compareTrigger.closeMenu()" [class.selected]="!form.get('compare_to')?.value">None</button>
        @for (o of compareOptions; track o.value) {
          <button mat-menu-item (click)="setField('compare_to', o.value); compareTrigger.closeMenu()" [class.selected]="form.get('compare_to')?.value === o.value">
            {{ o.label }}
          </button>
        }
      </div>
    </mat-menu>
  }

  <!-- Custom date range -->
  @if (config.dateRange || form.get('period')?.value === 'custom') {
    <div class="flex shrink-0 items-center gap-1.5">
      <div class="date-chip" (click)="fromPicker.open()">
        <mat-icon class="text-secondary/70 !h-3 !w-3 !text-xs !leading-none">calendar_today</mat-icon>
        <input [matDatepicker]="fromPicker" formControlName="date_from" placeholder="From" class="date-chip__input" readonly />
        <mat-datepicker #fromPicker />
      </div>
      <span class="text-secondary text-xs">–</span>
      <div class="date-chip" (click)="toPicker.open()">
        <mat-icon class="text-secondary/70 !h-3 !w-3 !text-xs !leading-none">calendar_today</mat-icon>
        <input [matDatepicker]="toPicker" formControlName="date_to" placeholder="To" class="date-chip__input" readonly />
        <mat-datepicker #toPicker />
      </div>
    </div>
  }

  <!-- Status chip-select -->
  @if (config.status) {
    <button type="button" class="filter-chip" [class.filter-chip--active]="form.get('status')?.value" #statusTrigger="matMenuTrigger" [matMenuTriggerFor]="statusMenu">
      <span class="filter-chip__label">Status</span>
      <span class="filter-chip__value">{{ selectedStatusLabel }}</span>
      <mat-icon class="filter-chip__arrow">expand_more</mat-icon>
    </button>
    <mat-menu #statusMenu="matMenu" panelClass="filter-chip-menu">
      <div style="max-height: 260px; overflow-y: auto; overflow-x: hidden">
        <button mat-menu-item (click)="setField('status', null); statusTrigger.closeMenu()" [class.selected]="!form.get('status')?.value">All</button>
        @for (s of statusOptions; track s.value) {
          <button mat-menu-item (click)="setField('status', s.value); statusTrigger.closeMenu()" [class.selected]="form.get('status')?.value === s.value">
            {{ s.label }}
          </button>
        }
      </div>
    </mat-menu>
  }

  <!-- Priority chip-select -->
  @if (config.priority) {
    <button type="button" class="filter-chip" [class.filter-chip--active]="form.get('priority')?.value" #priorityTrigger="matMenuTrigger" [matMenuTriggerFor]="priorityMenu">
      <span class="filter-chip__label">Priority</span>
      <span class="filter-chip__value">{{ selectedPriorityLabel }}</span>
      <mat-icon class="filter-chip__arrow">expand_more</mat-icon>
    </button>
    <mat-menu #priorityMenu="matMenu" panelClass="filter-chip-menu">
      <div style="max-height: 260px; overflow-y: auto; overflow-x: hidden">
        <button mat-menu-item (click)="setField('priority', null); priorityTrigger.closeMenu()" [class.selected]="!form.get('priority')?.value">All</button>
        @for (p of priorityOptions; track p.value) {
          <button mat-menu-item (click)="setField('priority', p.value); priorityTrigger.closeMenu()" [class.selected]="form.get('priority')?.value === p.value">
            {{ p.label }}
          </button>
        }
      </div>
    </mat-menu>
  }

  <!-- Reset -->
  <button
    type="button"
    (click)="reset()"
    title="Reset filters"
    class="text-secondary ml-auto flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-[11px] transition-colors hover:text-default hover:bg-gray-100"
  >
    <mat-icon class="!h-3.5 !w-3.5 !text-sm !leading-none">refresh</mat-icon>
    Reset
  </button>
</div>
